/**
 * @module DialogTitle
 *
 * @see https://0.vuetifyjs.com/components/disclosure/dialog
 *
 * @remarks
 * Title component for dialogs. Provides the accessible name for the dialog
 * via aria-labelledby. Should be used inside DialogContent.
 */

<script lang="ts">
  // Types
  import type { AtomProps } from '#v0/components/Atom'

  export interface DialogTitleProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
  }

  export interface DialogTitleSlotProps {
    /** Attributes to bind to the title element */
    attrs: {
      id: string
    }
  }
</script>

<script setup lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import { useDialogContext } from './DialogRoot.vue'

  // Utilities
  import { onBeforeUnmount, onMounted, toRef } from 'vue'

  defineOptions({ name: 'DialogTitle' })

  defineSlots<{
    default: (props: DialogTitleSlotProps) => any
  }>()

  const {
    as = 'h2',
    namespace = 'v0:dialog',
    renderless,
  } = defineProps<DialogTitleProps>()

  const context = useDialogContext(namespace)

  onMounted(() => {
    context.hasTitle.value = true
  })

  onBeforeUnmount(() => {
    context.hasTitle.value = false
  })

  const slotProps = toRef((): DialogTitleSlotProps => ({
    attrs: {
      id: context.titleId,
    },
  }))
</script>

<template>
  <Atom
    :as
    :renderless
    v-bind="slotProps.attrs"
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
